import { Box } from "@mui/material";
import { memo } from "react";
import { DragDropContext, Draggable, type DraggableProvidedDragHandleProps, Droppable } from "react-beautiful-dnd";

import TypeIcon from "@/Dashboard/components/MediaItem/TypeIcon";
import type { Asset, AssetCategory, ContentType } from "@/Dashboard/pages/AssetLibrary/types";
import type { MenuItem } from "@/VisualBuilder/components/LeftHandMenu/types";
import { EXPERIENCE_PRIMARY_FILTER } from "@/constants";
import { useTabStatus } from "@/hooks/useTabStatus";
import { useUrlParams } from "@/hooks/useUrlParams";
import { useThunkDispatch } from "@/store/hooks";
import {
  changeMenuItemsOrder,
  hideMenuItem,
  onChangeAssetCategoryStatus,
  onChangeTypeStatus,
  renameAssetCategory,
  renameMenuItem,
  sortMenuItems,
  toggleAssetCategoryOpen,
  toggleAssetCategorySelection,
  toggleContentTypeOpen,
  toggleContentTypeSelection,
} from "@/utils/drawerUtils";

import MenuAccordion from "./MenuAccordion";
import MenuAccordionItem from "./MenuAccordionItem";
import { hasStartingMediaItemInCategory, hasStartingMediaItemInList } from "./utils";

/**
 * Left hand menu props.
 */
interface LeftHandMenuProps {
  readonly assetCategories: (AssetCategory & { contentTypes: number[] })[];
  readonly assetCategoriesToggled: Record<number, boolean>;
  readonly assetCategoryFilterId?: number;
  readonly assetMenuOpen: boolean;
  readonly experienceAssetCategoryIds: Record<number, boolean>;
  readonly experienceContentTypeIds: Record<number, boolean>;
  readonly filteredAssetCategories: AssetCategory[];
  readonly filteredAssetCategoryMediaItems: Record<number, Asset[]>;
  readonly filteredContentTypes: ContentType[];
  readonly filteredContentTypeMediaItems: Record<number, Asset[]>;
  readonly hiddenAssetCategories: Record<number, boolean>;
  readonly hiddenContentTypes: Record<number, boolean>;
  readonly locked: boolean;
  readonly primaryFilter: EXPERIENCE_PRIMARY_FILTER;
  readonly typeFilterId?: number;
  readonly typesToggled: Record<number, boolean>;
  readonly experienceMenuItems: MenuItem[];
  readonly propertyMenuItems: MenuItem[];
  readonly assetCategoryNames?: Record<number, string>;
  readonly isDraggable?: boolean;
  readonly startingAssetId: number | null;
  readonly menuRefs: {
    setRef: (key: string) => (element: HTMLElement | null) => void;
    focusElement: (key: string) => void;
  };
}

/**
 * New left hand menu of the visual builder.
 */
function LeftHandMenu(props: LeftHandMenuProps) {
  const {
    assetCategories,
    assetCategoriesToggled,
    assetCategoryFilterId,
    assetMenuOpen,
    experienceAssetCategoryIds = {},
    experienceContentTypeIds,
    filteredAssetCategories = [],
    filteredAssetCategoryMediaItems = {},
    filteredContentTypes = [],
    filteredContentTypeMediaItems = {},
    hiddenAssetCategories,
    hiddenContentTypes,
    locked,
    primaryFilter,
    typeFilterId,
    typesToggled = {},
    assetCategoryNames = {},
    experienceMenuItems = [],
    propertyMenuItems = [],
    isDraggable = false,
    startingAssetId,
    menuRefs,
  } = props;

  const isContentTypeFilter = primaryFilter === EXPERIENCE_PRIMARY_FILTER.TYPE;

  const dispatch = useThunkDispatch();
  const params = useUrlParams();

  const { isTabCustomize, isPropertyBasedTab } = useTabStatus();

  const topList = isContentTypeFilter ? filteredContentTypes : filteredAssetCategories;
  const topItemType = isContentTypeFilter ? "content-type" : "asset-category";

  const menuItems = isPropertyBasedTab ? propertyMenuItems : experienceMenuItems;

  const sortedTopList = sortMenuItems({
    parentId: 0,
    itemType: topItemType,
    items: topList,
    menuItems,
  }) as AssetCategory[] | ContentType[];

  /**
   * Toggles the visibility of a content type.
   */
  const toggleContentTypeVisibility = (contentType: ContentType) => {
    if (locked) return;
    void onChangeTypeStatus({
      contentType,
      dispatch,
      experienceContentTypeIds,
      hiddenContentTypes,
      params,
      typeFilterId,
    });
  };

  /**
   * Selects an asset category.
   * This can be called either in a subsection or as the title of a section.
   */
  const toggleAssetCategorySelection2 = (
    category: AssetCategory,
    parentType?: ContentType,
    currentTypeFilterId?: number,
  ) => {
    if (isTabCustomize && locked) return;
    toggleAssetCategorySelection({
      assetCategoryFilterId,
      assetMenuOpen,
      category,
      params,
      parentType,
      primaryFilter,
      typeFilterId: currentTypeFilterId ?? typeFilterId,
    });
  };

  /**
   * Selects a content type.
   * This can be called either in a subsection or as the title of a section.
   */
  const toggleContentTypeSelection2 = (
    type: ContentType,
    parentCategory?: AssetCategory,
    currentCategoryFilterId?: number,
  ) => {
    if (isTabCustomize && locked) return;

    toggleContentTypeSelection({
      assetCategoryFilterId: currentCategoryFilterId ?? assetCategoryFilterId,
      assetMenuOpen,
      params,
      parentCategory,
      primaryFilter,
      type,
      typeFilterId,
    });
  };

  /**
   * Renders a single content type accordion.
   */
  const renderContentTypeAccordion = (
    dragHandleProps: DraggableProvidedDragHandleProps,
    type: ContentType,
    items: AssetCategory[],
  ) => {
    const itemType = "asset-category";
    const parentId = type.id;

    const filteredItems = items.filter((category) => {
      return filteredContentTypeMediaItems[type.id]?.some((item) => item.assetCategoryId === category.id);
    });

    if (filteredItems.length === 0) {
      return null;
    }

    const hasStartingMediaItem = hasStartingMediaItemInList(
      items,
      filteredAssetCategoryMediaItems,
      startingAssetId,
      type.id,
    );

    return (
      <MenuAccordion
        dragHandleProps={dragHandleProps}
        hidden={hiddenContentTypes[type.id]}
        icon={type.id !== -1 && <TypeIcon type={type.value} />}
        name={type.name}
        onDragItems={(result) => {
          void changeMenuItemsOrder({
            result,
            params,
            menuItems,
            items: filteredItems,
            itemType,
            parentId,
            isPropertyBasedTab,
          });
        }}
        onToggleOpen={() =>
          toggleContentTypeOpen({
            params,
            type,
            typesToggled,
            locked,
            isInCustomizeMode: isTabCustomize,
          })
        }
        onToggleSelection={() => toggleContentTypeSelection2(type, undefined, filteredItems[0]?.id)}
        onToggleVisibility={() => toggleContentTypeVisibility(type)}
        open={typesToggled[type.id]}
        selected={typeFilterId === type.id}
        showDragHandle={props.isDraggable}
        showVisibilityButton={isTabCustomize}
        locked={locked}
        menuRefs={menuRefs}
        assetCategoryFilterId={type.id}
        primaryFilter={primaryFilter}
        showStartingPointFlag={hasStartingMediaItem}
      >
        {filteredItems.map((category, index) => {
          const sectionItem = experienceMenuItems.find(
            (section) =>
              section.parentId === parentId && section.itemId === category.id && section.itemType === itemType,
          );

          const hasStartingMediaItem = hasStartingMediaItemInCategory(
            filteredAssetCategoryMediaItems[category.id],
            startingAssetId,
            type.id,
          );

          return (
            <MenuAccordionItem
              id={category.id}
              index={index}
              key={category.id}
              name={sectionItem?.name || category.name}
              data-testid="accordion-item"
              onToggleSelection={() => toggleAssetCategorySelection2(category, type)}
              onToggleVisibility={() =>
                void hideMenuItem({
                  assetCategory: category,
                  contentType: type,
                  itemType,
                  params,
                  experienceMenuItems,
                  locked,
                })
              }
              onRename={(value) =>
                void renameMenuItem({
                  assetCategory: category,
                  contentType: type,
                  itemType,
                  params,
                  experienceMenuItems,
                  value,
                })
              }
              selected={typeFilterId === type.id && assetCategoryFilterId === category.id}
              showVisibilityButton={isTabCustomize}
              showDragHandle={isDraggable}
              showRenameButton={isTabCustomize}
              hidden={sectionItem?.hidden}
              locked={locked}
              context="type"
              typeFilterId={type.id}
              assetCategoryFilterId={category.id}
              primaryFilter={primaryFilter}
              menuRefs={menuRefs}
              showStartingPointFlag={hasStartingMediaItem}
            />
          );
        })}
      </MenuAccordion>
    );
  };

  /**
   * Renders all the content type accordions.
   */
  const renderContentTypeAccordions = () => {
    const contentTypes = sortedTopList as ContentType[];
    return contentTypes
      .filter((type) => (isTabCustomize ? true : !hiddenContentTypes[type.id]))
      .map((type, index) => {
        const items = assetCategories.filter((category) => {
          const hidden = isTabCustomize
            ? false
            : experienceMenuItems.find(
                (section) =>
                  section.parentId === type.id &&
                  section.itemId === category.id &&
                  section.itemType === "asset-category",
              )?.hidden;

          return !hidden && category.contentTypes?.includes(type.id);
        });

        const sortedItems = sortMenuItems({
          parentId: type.id,
          itemType: "asset-category",
          items,
          menuItems,
        }) as AssetCategory[];

        if (sortedItems.length === 0 || filteredContentTypeMediaItems[type.id].length === 0) {
          return null;
        }

        return (
          <Draggable draggableId={String(type.id)} index={index} isDragDisabled={!isDraggable || locked} key={type.id}>
            {(providedDraggable) => (
              <Box {...providedDraggable.draggableProps} ref={providedDraggable.innerRef} tabIndex={-1}>
                {renderContentTypeAccordion(providedDraggable.dragHandleProps!, type, sortedItems)}
              </Box>
            )}
          </Draggable>
        );
      });
  };

  /**
   * Renders a single asset category accordion.
   */
  const renderAssetCategoryAccordion = (
    dragHandleProps: DraggableProvidedDragHandleProps,
    assetCategory: AssetCategory,
    items: ContentType[],
  ) => {
    const itemType = "content-type";
    const parentId = assetCategory.id;
    const contentTypeIds = items.map((ct) => ct.id);
    const hasStartingMediaItem = hasStartingMediaItemInCategory(
      filteredAssetCategoryMediaItems[assetCategory.id],
      startingAssetId,
      contentTypeIds,
    );

    return (
      <MenuAccordion
        dragHandleProps={dragHandleProps}
        hidden={hiddenAssetCategories[assetCategory.id]}
        name={assetCategory.name}
        data-testid="accordion-item"
        onDragItems={(result) => {
          void changeMenuItemsOrder({
            result,
            params,
            menuItems,
            items,
            itemType,
            parentId,
            isPropertyBasedTab,
          });
        }}
        onRename={(value) =>
          void renameAssetCategory({
            assetCategory,
            assetCategoryNames,
            experienceAssetCategoryIds,
            params,
            value,
          })
        }
        onToggleOpen={() =>
          toggleAssetCategoryOpen({
            params,
            category: assetCategory,
            assetCategoriesToggled,
            locked,
            isInCustomizeMode: isTabCustomize,
          })
        }
        onToggleSelection={() => toggleAssetCategorySelection2(assetCategory, undefined, items[0]?.id)}
        onToggleVisibility={() =>
          void onChangeAssetCategoryStatus({
            assetCategory,
            assetCategoryFilterId,
            experienceAssetCategoryIds,
            hiddenAssetCategories,
            params,
            locked,
          })
        }
        open={assetCategoriesToggled[assetCategory.id]}
        selected={assetCategoryFilterId === assetCategory.id}
        showDragHandle={isDraggable}
        showRenameButton={isTabCustomize && assetCategory.id !== -1}
        showVisibilityButton={isTabCustomize && assetCategory.id !== -1}
        menuRefs={menuRefs}
        assetCategoryFilterId={assetCategory.id}
        primaryFilter={primaryFilter}
        locked={locked}
        showStartingPointFlag={hasStartingMediaItem}
      >
        {items.map((type, index) => {
          const sectionItem = experienceMenuItems.find(
            (section) =>
              section.parentId === assetCategory.id && section.itemId === type.id && section.itemType === itemType,
          );

          const hasStartingMediaItem = hasStartingMediaItemInCategory(
            filteredAssetCategoryMediaItems[assetCategory.id],
            startingAssetId,
            type.id,
          );

          return (
            <MenuAccordionItem
              icon={<TypeIcon type={type.value} />}
              id={type.id}
              index={index}
              key={type.id}
              name={sectionItem?.name || type.name + ""}
              onToggleSelection={() => toggleContentTypeSelection2(type, assetCategory)}
              onToggleVisibility={() =>
                void hideMenuItem({
                  assetCategory,
                  contentType: type,
                  itemType,
                  params,
                  experienceMenuItems,
                  locked,
                })
              }
              selected={typeFilterId === type.id && assetCategoryFilterId === assetCategory.id}
              showDragHandle={isDraggable}
              showVisibilityButton={isTabCustomize}
              hidden={sectionItem?.hidden}
              locked={locked}
              context="category"
              typeFilterId={type.id}
              assetCategoryFilterId={assetCategory.id}
              primaryFilter={primaryFilter}
              menuRefs={menuRefs}
              showStartingPointFlag={hasStartingMediaItem}
            />
          );
        })}
      </MenuAccordion>
    );
  };

  /**
   * Renders all the asset category accordions.
   */
  const renderAssetCategoryAccordions = () => {
    const assetCategories = sortedTopList as AssetCategory[];
    return assetCategories
      ?.filter((category) => (isTabCustomize ? true : !hiddenAssetCategories[category.id]))
      .map((category, index) => {
        const contentTypes: ContentType[] = [];
        const previousTypes: number[] = [];

        const filteredMediaItems = filteredAssetCategoryMediaItems[category.id] || [];

        filteredMediaItems.forEach((item) => {
          const contentType = item.contentType;

          if (!previousTypes.includes(item.contentTypeId)) {
            contentTypes.push(contentType);
            previousTypes.push(item.contentTypeId);
          }
        });

        const items = contentTypes.filter((type) => {
          const hidden = isTabCustomize
            ? false
            : experienceMenuItems.find(
                (section) =>
                  section.parentId === category.id && section.itemId === type.id && section.itemType === "content-type",
              )?.hidden;
          return !hidden;
        });

        const sortedItems = sortMenuItems({
          parentId: category.id,
          itemType: "content-type",
          items,
          menuItems,
        }) as ContentType[];
        if (sortedItems.length === 0) {
          return null;
        }

        return (
          <Draggable
            draggableId={String(category.id)}
            index={index}
            isDragDisabled={!isDraggable || locked}
            key={category.id}
          >
            {(providedDraggable) => (
              <Box {...providedDraggable.draggableProps} ref={providedDraggable.innerRef} tabIndex={-1}>
                {renderAssetCategoryAccordion(providedDraggable.dragHandleProps!, category, sortedItems)}
              </Box>
            )}
          </Draggable>
        );
      });
  };

  return (
    <Box data-testid="left-hand-menu" sx={{ margin: "0px 16px", marginBottom: "32px" }}>
      <DragDropContext
        onDragEnd={(result) => {
          void changeMenuItemsOrder({
            result,
            items: sortedTopList,
            menuItems,
            itemType: topItemType,
            parentId: 0,
            params,
            isPropertyBasedTab,
          });
        }}
      >
        <Droppable droppableId="left-hand-menu" isDropDisabled={!isDraggable || locked}>
          {(provided) => (
            <Box {...provided.droppableProps} ref={provided.innerRef}>
              {primaryFilter === EXPERIENCE_PRIMARY_FILTER.TYPE
                ? renderContentTypeAccordions()
                : renderAssetCategoryAccordions()}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
}

export default memo(LeftHandMenu);
